// src/pages/admin/BankFeedCard.jsx
import { useRef } from "react";

// Status pill tones. "ok" covers a live NetSuite feed and an up-to-date CSV;
// "warn" is a CSV bank whose export is stale.
const TONES = {
  ok: {
    pill: "bg-palette-emerald-soft text-palette-emerald-hover",
    dot: "bg-palette-emerald-hover",
    bar: "bg-palette-emerald-badge",
  },
  warn: {
    pill: "bg-palette-amber-soft text-palette-amber-primary",
    dot: "bg-palette-amber-primary",
    bar: "bg-palette-amber-badge",
  },
};

function SyncIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M23 4v6h-6" />
      <path d="M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="M17 8l-5-5-5 5" />
      <path d="M12 3v12" />
    </svg>
  );
}

const formatAmount = (n) =>
  Number(n || 0).toLocaleString("en-CA", { style: "currency", currency: "CAD" });

/**
 * BankFeedCard — one bank's deposit feed: source, match progress, and the
 * action that refreshes it (Sync now for the NetSuite feed, Upload CSV for the
 * rest). Actions render disabled until a handler is passed.
 */
export default function BankFeedCard({
  code,
  name,
  source,
  feed = "csv", // "netsuite" | "csv"
  status,
  tone = "ok",
  // Match stats are optional: until bank lines are loaded the card shows only
  // what the account list knows (practices / accounts on this bank).
  matched = null,
  total = null,
  practices = 0,
  accounts = null,
  amount = null,
  footnote,
  footnoteTitle,
  onSync,
  onUpload,
}) {
  const fileRef = useRef(null);
  const t = TONES[tone] || TONES.ok;
  const hasLines = total != null;
  const pct = total > 0 ? Math.round(((matched || 0) / total) * 100) : 0;
  const isNetsuite = feed === "netsuite";
  const action = isNetsuite ? onSync : onUpload;

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-2xl border border-palette-slate-divider bg-white px-4 pb-3 pt-3.5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className={`inline-flex h-[38px] w-[38px] flex-none items-center justify-center rounded-lg border text-[11.5px] font-bold tracking-wider ${
            isNetsuite
              ? "border-palette-emerald-primary bg-palette-emerald-primary text-white"
              : "border-palette-slate-divider bg-palette-slate-muted text-palette-slate-heading"
          }`}
        >
          {code}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[14.5px] font-semibold text-palette-slate-primary">{name}</div>
          <div className="truncate text-[11.5px] text-palette-slate-placeholder">{source}</div>
        </div>
        <span
          className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold ${t.pill}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
          {status}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[12.5px] text-palette-slate-secondary">
            {hasLines ? `${matched || 0} of ${total} lines matched` : "No bank lines loaded"}
          </span>
          {hasLines && (
            <span className="ml-auto text-[13px] font-semibold tabular-nums text-palette-slate-primary">
              {pct}%
            </span>
          )}
        </div>
        <span className="block h-1.5 overflow-hidden rounded-full bg-palette-slate-divider">
          <span
            className={`block h-full rounded-full transition-[width] duration-500 ${t.bar}`}
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className="text-[11.5px] tabular-nums text-palette-slate-placeholder">
          {[
            `${practices} ${practices === 1 ? "practice" : "practices"}`,
            accounts != null && `${accounts} ${accounts === 1 ? "account" : "accounts"}`,
            amount != null && formatAmount(amount),
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-palette-slate-muted pt-2.5">
        <span
          title={footnoteTitle}
          className="min-w-0 flex-[1_1_140px] text-xs text-palette-slate-secondary"
        >
          {footnote}
        </span>
        <button
          type="button"
          disabled={!action}
          onClick={isNetsuite ? onSync : () => fileRef.current?.click()}
          className="inline-flex h-[30px] items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-palette-slate-divider bg-white px-3 text-[13px] font-medium text-palette-emerald-hover shadow-sm transition-colors hover:border-palette-emerald-border hover:bg-palette-emerald-soft disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isNetsuite ? <SyncIcon /> : <UploadIcon />}
          {isNetsuite ? "Sync now" : "Upload CSV"}
        </button>
        {!isNetsuite && (
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) onUpload?.(file);
            }}
          />
        )}
      </div>
    </div>
  );
}
